import React, { useCallback, useEffect, useMemo, useState } from "react";
import UserProfile from "./components/UserProfile";
import RideHistory from "./components/RideHistory";
import { getRideHistory, verifyUser, requestRide, acceptBid, rejectBid, updateRideDetails, updateRiderProfile, getActiveRide, getMatchedRide, cancelRide, completeRideByRider, deleteRiderAccount, registerRider, sendOtp, verifyOtp, setAuthToken, clearAuthToken } from "./services/api";
import RequestRideModal from "./components/RequestRideModal";
import ViewBidsModal from "./components/ViewBidsModal";
import EditProfileModal from "./components/EditProfileModal";
import MatchedRideModal from "./components/MatchedRideModal";

const App = () => {
  const [profile, setProfile] = useState(null);
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isTelegramContext, setIsTelegramContext] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submittingRide, setSubmittingRide] = useState(false);
  const [bidsModalRide, setBidsModalRide] = useState(null);
  const [editingRide, setEditingRide] = useState(null);
  const [submittingBidAction, setSubmittingBidAction] = useState(false);
  const [showMatchedModal, setShowMatchedModal] = useState(false);
  // Per-modal API error messages
  const [createRideError, setCreateRideError] = useState(null);
  const [updateRideError, setUpdateRideError] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [registered, setRegistered] = useState(true);
  // Browser email OTP login
  const [browserEmail, setBrowserEmail] = useState("");
  const [browserOtp, setBrowserOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [browserLoginError, setBrowserLoginError] = useState(null);

  const telegramWebApp = useMemo(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    return window.Telegram?.WebApp;
  }, []);

  const loadRideHistory = useCallback(
    async (telegramId) => {
      if (!telegramId) {
        return;
      }

      setRefreshing(true);
      try {
        const historyResponse = await getRideHistory(telegramId);
        if (!historyResponse?.success) {
          throw new Error(historyResponse?.error || "Failed to load ride history");
        }
        setRides(historyResponse.data || []);

        // Also load active ride summary
        const activeResp = await getActiveRide(telegramId);
        if (activeResp?.success) {
          setActiveRide(activeResp.data || null);
        }
      } catch (err) {
        // Treat 404 as no rides for first-time users
        if (err?.status === 404) {
          setRides([]);
          setError(null);
          setActiveRide(null);
        } else {
          console.error("Ride history fetch failed:", err);
          setError(err.message || "Failed to load ride history");
        }
      } finally {
        setRefreshing(false);
      }
    },
    []
  );

  const bootstrap = useCallback(async () => {
    if (!telegramWebApp || !telegramWebApp.initData) {
      setIsTelegramContext(false);
      setLoading(false);
      setError(null); // Clear any error when not in Telegram
      // Try restore browser session from storage
      try {
        const storedToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_rider_token") : null;
        const storedProfile = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_rider_profile") : null;
        const storedRides = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_rider_rides") : null;
        const storedRegistered = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("miniapp_rider_registered") : null;
        if (storedToken && storedProfile) {
          setAuthToken(storedToken);
          setProfile(JSON.parse(storedProfile));
          setRides(storedRides ? JSON.parse(storedRides) : []);
          setRegistered(storedRegistered !== "false");
          setError(null);
          const profileData = JSON.parse(storedProfile);
          if (profileData?.telegramId) {
            await loadRideHistory(profileData.telegramId);
          }
        } else {
          // No stored session - ensure profile is null and error is cleared
          setProfile(null);
          setError(null);
        }
      } catch (_) {
        // On error, ensure we show OTP screen
        setProfile(null);
        setError(null);
      }
      return;
    }

    setIsTelegramContext(true);
    setLoading(true);
    setError(null);

    try {
      telegramWebApp.ready?.();
      telegramWebApp.expand?.();

      const unsafeUser = telegramWebApp.initDataUnsafe?.user;
      if (unsafeUser) {
        setProfile((current) => {
          if (current) return current;
          const displayName = [unsafeUser.first_name, unsafeUser.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
          return {
            telegramId: unsafeUser.id?.toString?.() || "",
            name: displayName || unsafeUser.username || "",
            telegramUsername: unsafeUser.username || `tg_${unsafeUser.id}`,
            phoneNumber: "",
            homeAddress: "",
            workAddress: "",
            rating: 0,
            totalRides: 0,
            completedRides: 0,
            successRate: 0,
          };
        });
      }

      const initData = telegramWebApp.initData;
      const verification = await verifyUser(initData);

      if (!verification?.success) {
        throw new Error(verification?.error || "Unable to verify Telegram user");
      }

      const profileData = verification.data?.profile;
      setProfile(profileData || null);
      setRides(verification.data?.rides || []);
      setRegistered(Boolean(verification.data?.registered));

      if (profileData?.telegramId) {
        await loadRideHistory(profileData.telegramId);
      }
    } catch (err) {
      console.error("Bootstrap failed:", err);
      setError(err.message || "Something went wrong while loading your data.");
    } finally {
      setLoading(false);
    }
  }, [loadRideHistory, telegramWebApp]);

  useEffect(() => {
    bootstrap();
    // Also check on mount if we're not in Telegram
    if (typeof window !== "undefined" && (!window.Telegram?.WebApp || !window.Telegram?.WebApp?.initData)) {
      setIsTelegramContext(false);
    }
  }, [bootstrap]);

  // Show loading animation while bootstrapping the home page
  if (loading) {
    return (
      <div className="min-h-screen bw-dark-root flex items-center justify-center p-6">
        {/* LottieFiles Web Component */}
        <dotlottie-wc
          src="https://lottie.host/079bbb73-782c-4c3d-b923-b31d36cc45ea/YRG7X6HxkR.lottie"
          style={{ width: 300, height: 300 }}
          autoplay
          loop
        />
      </div>
    );
  }

  // Browser email OTP login when opened outside Telegram and no profile
  if (!isTelegramContext && !profile) {
    // Clear any old error messages
    if (error) {
      setError(null);
    }
    const handleSendOtp = async (e) => {
      e.preventDefault();
      const email = browserEmail.trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setBrowserLoginError("Please enter a valid email address.");
        return;
      }
      setBrowserLoginError(null);
      setOtpSubmitting(true);
      try {
        const res = await sendOtp(email, "rider");
        if (res?.success) {
          setOtpSent(true);
          setBrowserOtp("");
          setBrowserLoginError(null);
        } else {
          setBrowserLoginError(res?.error || "Could not send code. Try again.");
        }
      } catch (err) {
        setBrowserLoginError(err?.message || "Could not send code. Try again.");
      } finally {
        setOtpSubmitting(false);
      }
    };

    const handleVerifyOtp = async (e) => {
      e.preventDefault();
      const email = browserEmail.trim().toLowerCase();
      const otp = String(browserOtp).trim();
      if (!email || !otp) {
        setBrowserLoginError("Please enter the code from your email.");
        return;
      }
      setBrowserLoginError(null);
      setOtpSubmitting(true);
      try {
        const res = await verifyOtp(email, otp, "rider");
        if (res?.success && res.token && res.data?.profile) {
          setAuthToken(res.token);
          setProfile(res.data.profile);
          setRides(res.data.rides || []);
          setRegistered(Boolean(res.data.registered));
          setError(null);
          try {
            sessionStorage.setItem("miniapp_rider_token", res.token);
            sessionStorage.setItem("miniapp_rider_profile", JSON.stringify(res.data.profile));
            sessionStorage.setItem("miniapp_rider_rides", JSON.stringify(res.data.rides || []));
            sessionStorage.setItem("miniapp_rider_registered", String(res.data.registered !== false));
          } catch (_) {}
          if (res.data.profile?.telegramId) {
            await loadRideHistory(res.data.profile.telegramId);
          }
        } else {
          setBrowserLoginError(res?.error || "Invalid or expired code.");
        }
      } catch (err) {
        setBrowserLoginError(err?.message || "Invalid or expired code.");
      } finally {
        setOtpSubmitting(false);
      }
    };

    const handleLogout = () => {
      clearAuthToken();
      setProfile(null);
      setRides([]);
      setOtpSent(false);
      setBrowserOtp("");
      setBrowserLoginError(null);
      try {
        sessionStorage.removeItem("miniapp_rider_token");
        sessionStorage.removeItem("miniapp_rider_profile");
        sessionStorage.removeItem("miniapp_rider_rides");
        sessionStorage.removeItem("miniapp_rider_registered");
      } catch (_) {}
    };

    return (
      <div className="min-h-screen bw-dark-root p-6">
        <div className="mx-auto max-w-md space-y-6">
          <h1 className="font-display text-3xl font-bold text-center">RideEase</h1>
          <p className="bw-muted text-center text-sm">
            Enter the email you added in the app when using it in Telegram to log in.
          </p>
          {browserLoginError && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {browserLoginError}
            </div>
          )}
          {!otpSent ? (
            <form onSubmit={handleSendOtp} className="space-y-3">
              <label className="block text-sm font-medium text-white/80">Email</label>
              <input
                type="email"
                className="bw-input w-full"
                value={browserEmail}
                onChange={(e) => setBrowserEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={otpSubmitting}
              />
              <button type="submit" className="bw-btn w-full" disabled={otpSubmitting}>
                {otpSubmitting ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <label className="block text-sm font-medium text-white/80">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="bw-input w-full"
                value={browserOtp}
                onChange={(e) => setBrowserOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                disabled={otpSubmitting}
              />
              <button type="submit" className="bw-btn w-full" disabled={otpSubmitting}>
                {otpSubmitting ? "Verifying…" : "Log in"}
              </button>
              <button
                type="button"
                className="bw-btn-ghost w-full text-sm"
                onClick={() => { setOtpSent(false); setBrowserOtp(""); setBrowserLoginError(null); }}
              >
                Use a different email
              </button>
            </form>
          )}
          <div className="bw-divider pt-6 mt-6 space-y-3 text-center">
            <p className="bw-muted text-sm">New to RideEase?</p>
            <a
              href="/signup"
              className="bw-btn w-full inline-block text-center"
            >
              Create an account on our website
            </a>
            <a
              href="/"
              className="bw-btn-ghost w-full inline-block text-center text-sm"
            >
              Visit homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  const handleRefresh = async () => {
    if (!profile?.telegramId) {
      await bootstrap();
      return;
    }

    await loadRideHistory(profile.telegramId);
  };

  const handleRequestRide = async (payload) => {
    try {
      setSubmittingRide(true);
      setError(null);
      setCreateRideError(null);
      const initData = telegramWebApp?.initData;
      const res = await requestRide({ initData, ...payload });
      if (!res?.success) throw new Error(res?.error || "Failed to request ride");
      // Refresh ride history
      if (profile?.telegramId) {
        await loadRideHistory(profile.telegramId);
      }
      setShowRequestModal(false);
    } catch (e) {
      console.error("Request ride failed:", e);
      // Keep the modal open; show error inside modal instead of home
      setCreateRideError(e.message || "Failed to request ride");
    } finally {
      setSubmittingRide(false);
    }
  };

  const onViewBids = (ride) => setBidsModalRide(ride);
  const onEditRide = (ride) => setEditingRide(ride);

  const handleAcceptBid = async (bid) => {
    try {
      setSubmittingBidAction(true);
      const initData = telegramWebApp?.initData;
      const res = await acceptBid(bid.id, initData);
      if (!res?.success) throw new Error(res?.error || "Failed to accept bid");
      setBidsModalRide(null);
      if (profile?.telegramId) await loadRideHistory(profile.telegramId);
    } catch (e) {
      setError(e.message || 'Failed to accept bid');
    } finally {
      setSubmittingBidAction(false);
    }
  };

  const handleRejectBid = async (bid) => {
    try {
      setSubmittingBidAction(true);
      const initData = telegramWebApp?.initData;
      const res = await rejectBid(bid.id, initData);
      if (!res?.success) throw new Error(res?.error || "Failed to reject bid");
      // Keep modal open and refresh list
      if (bidsModalRide?.id) {
        setBidsModalRide({ ...bidsModalRide }); // trigger re-fetch in modal
      }
    } catch (e) {
      setError(e.message || 'Failed to reject bid');
    } finally {
      setSubmittingBidAction(false);
    }
  };

  const handleUpdateRide = async ({ pickup, dropoff, rideTime, bid }) => {
    if (!editingRide?.id) return;
    try {
      setSubmittingRide(true);
      setUpdateRideError(null);
      const initData = telegramWebApp?.initData;
      const res = await updateRideDetails(editingRide.id, initData, { pickup, dropoff, rideTime, bid });
      if (!res?.success) throw new Error(res?.error || 'Failed to update ride');
      setEditingRide(null);
      if (profile?.telegramId) await loadRideHistory(profile.telegramId);
    } catch (e) {
      setUpdateRideError(e.message || 'Failed to update ride');
    } finally {
      setSubmittingRide(false);
    }
  };

  const handleCancelActiveRide = async (ride) => {
    if (!ride?.id) return;
    if (!window.confirm("Cancel current ride?")) return;
    try {
      const initData = telegramWebApp?.initData;
      const res = await cancelRide(ride.id, initData);
      if (!res?.success) throw new Error(res?.error || 'Failed to cancel ride');
      if (profile?.telegramId) await loadRideHistory(profile.telegramId);
    } catch (e) {
      setError(e.message || 'Failed to cancel ride');
    }
  };

  const handleViewActiveDetails = async (ride) => {
    if (!ride) return;
    setShowMatchedModal(true);
  };

  const handleCompleteActiveRide = async (ride) => {
    if (!ride?.id) return;
    if (!window.confirm("Mark this ride as completed?")) return;
    try {
      const initData = telegramWebApp?.initData;
      const res = await completeRideByRider(ride.id, initData);
      if (!res?.success) throw new Error(res?.error || 'Failed to complete ride');
      if (profile?.telegramId) await loadRideHistory(profile.telegramId);
    } catch (e) {
      setError(e.message || 'Failed to complete ride');
    }
  };

  const formatDateSafe = (value) => {
    if (!value) return '—';
    try { return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)); } catch { return String(value); }
  };

  const handleBrowserLogout = () => {
    clearAuthToken();
    setProfile(null);
    setRides([]);
    setActiveRide(null);
    try {
      sessionStorage.removeItem("miniapp_rider_token");
      sessionStorage.removeItem("miniapp_rider_profile");
      sessionStorage.removeItem("miniapp_rider_rides");
      sessionStorage.removeItem("miniapp_rider_registered");
    } catch (_) {}
  };

  const handleSaveProfile = async (payload) => {
    try {
      setProfileError(null);
      setSubmittingRide(true);
      const initData = telegramWebApp?.initData;
      // If not registered, allow explicit register with privacy consent
      if (!registered) {
        const res = await registerRider(initData, { ...payload, acceptPrivacy: true });
        if (!res?.success) throw new Error(res?.error || 'Failed to register');
        if (res?.data?.profile) {
          setProfile(res.data.profile);
          setRegistered(true);
        }
      } else {
        const res = await updateRiderProfile(initData, payload);
        if (!res?.success) throw new Error(res?.error || 'Failed to update profile');
        if (res?.data?.profile) {
          setProfile(res.data.profile);
          try {
            sessionStorage.setItem("miniapp_rider_profile", JSON.stringify(res.data.profile));
          } catch (_) {}
        }
      }
      if (initData) {
        const verification = await verifyUser(initData);
        if (verification?.success) {
          setProfile(verification.data.profile);
          setRegistered(Boolean(verification.data?.registered));
        }
      }
      setShowEditProfile(false);
    } catch (e) {
      setProfileError(e.message || 'Failed to save profile');
    } finally {
      setSubmittingRide(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure to delete your account? This action cannot be undone.')) return;
    try {
      const initData = telegramWebApp?.initData;
      const res = await deleteRiderAccount(initData);
      if (!res?.success) throw new Error(res?.error || 'Failed to delete account');
      // Reset to unregistered state
      setRides([]);
      setActiveRide(null);
      setRegistered(false);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to delete account');
    }
  };

  return (
    <div className="min-h-screen bw-dark-root p-4">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="text-center space-y-3">
          <h1 className="font-display text-3xl font-bold tracking-tight">RideEase</h1>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {registered && !activeRide && (
              <button
                type="button"
                onClick={() => { setCreateRideError(null); setShowRequestModal(true); }}
                className="bw-btn"
                disabled={loading}
              >
                Request Ride
              </button>
            )}
            {registered && (
              <button
                type="button"
                onClick={handleRefresh}
                className="bw-btn"
                disabled={refreshing || loading}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setProfileError(null); setShowEditProfile(true); }}
              className={registered ? "bw-btn-ghost" : "bw-btn"}
            >
              {registered ? 'Update Profile' : 'Register'}
            </button>
            {!isTelegramContext && (
              <button type="button" onClick={handleBrowserLogout} className="bw-btn-ghost text-white/70">
                Log out
              </button>
            )}
          </div>
        </header>

        {error && (
          <div className="rounded-md border border-white/20 bg-transparent px-4 py-3 text-sm bw-muted italic">
            {error}
          </div>
        )}

        {!registered ? (
          <div className="mx-auto max-w-md rounded-xl border border-white/10 bg-neutral-950/40 p-4 text-sm text-white/80">
            <p className="mb-2">Complete your profile to start requesting rides.</p>
            <ul className="list-disc pl-5 space-y-1 text-white/70">
              <li>Name</li>
              <li>Phone</li>
              <li className="text-white/60">Optional: Home and Work address for quick picks</li>
            </ul>
            <div className="mt-3">
              <button type="button" className="bw-btn" onClick={() => { setProfileError(null); setShowEditProfile(true); }}>
                Open {registered ? 'Update' : 'Register'} Form
              </button>
            </div>
          </div>
        ) : (
          <>
            <UserProfile profile={profile} loading={loading} />
            <RideHistory
              rides={rides}
              loading={loading}
              refreshing={refreshing}
              onEditRide={onEditRide}
              onViewBids={onViewBids}
              activeRide={activeRide}
              onCancelActive={handleCancelActiveRide}
              onViewActiveDetails={handleViewActiveDetails}
              onCompleteActive={handleCompleteActiveRide}
            />
          </>
        )}
      </div>

      <RequestRideModal
        open={showRequestModal}
        onClose={() => { setShowRequestModal(false); setCreateRideError(null); }}
        onSubmit={handleRequestRide}
        submitting={submittingRide}
        profile={profile}
        apiError={createRideError}
      />

      <RequestRideModal
        open={!!editingRide}
        onClose={() => { setEditingRide(null); setUpdateRideError(null); }}
        onSubmit={handleUpdateRide}
        submitting={submittingRide}
        defaultBid={editingRide?.bid}
        profile={profile}
        apiError={updateRideError}
      />

      <EditProfileModal
        open={showEditProfile}
        onClose={() => { setShowEditProfile(false); setProfileError(null); }}
        onSubmit={handleSaveProfile}
        submitting={submittingRide}
        profile={profile}
        apiError={profileError}
        registered={registered}
        onDeleteAccount={handleDeleteAccount}
      />

      <MatchedRideModal
        open={showMatchedModal}
        onClose={() => setShowMatchedModal(false)}
        telegramId={profile?.telegramId}
        onCancel={handleCancelActiveRide}
        rideId={activeRide?.id}
      />

      <ViewBidsModal
        open={!!bidsModalRide}
        onClose={() => setBidsModalRide(null)}
        ride={bidsModalRide}
        onAccept={handleAcceptBid}
        onReject={handleRejectBid}
        loading={submittingBidAction}
      />
    </div>
  );
};

export default App;
